import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/toastMessages/toastProvider";

import {
  Typography,
  Divider,
  Stack,
  Box,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";

import getApiData from "../../../../hooks/apiRequests/getRequest";
import deleteApiData from "../../../../hooks/apiRequests/deleteRequest";
import putApiData from "../../../../hooks/apiRequests/putRequest";

import Userdetails from "./userdetails/userdetails";
import ForceSetPassword from "./userFeatures/passwords";
import ResetPassword from "./userFeatures/resetPassword";
import DeleteUser from "./userFeatures/deleteUsers";
import { theme } from "../../../../configs/theme";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const ActionRow = ({ title, description, action }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "stretch", sm: "center" }}
    justifyContent="space-between"
    spacing={1.5}
    sx={{
      bgcolor: "#fff",
      borderRadius: "10px",
      border: "1px solid",
      borderColor: "divider",
      p: 1.5,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}>
      {action}
    </Box>
  </Stack>
);

const PROTECTED_USER_EMAIL = "luke.burrowes@versite.co.uk";

const EditUser = () => {
  const [userInfo, setUserInfo] = useState();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const isDeleteDisabled =
    userInfo?.email?.toLowerCase() === PROTECTED_USER_EMAIL;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getApiData(`/User/Specific?UserId=${id}`);
        setUserInfo(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Failed to load data. Redirecting to login...", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, showToast]);

  const updateProfile = async (body) => {
    try {
      const response = await putApiData("/Update/User", body);
      if (response?.data) {
        setUserInfo(response.data);
        showToast("Profile Updated Successfully!", "success");
      } else {
        showToast("Unable to updated profile", "error");
      }
    } catch (error) {
      console.error(`Error processing updated request:`, error);
      showToast("Unable to delete profile", "error");
    }
  };

  const deleteUser = async () => {
    try {
      const response = await deleteApiData(`/Delete/User?UserId=${id}`);
      if (response?.data) {
        showToast("User Removed Successfully!", "success");
        const lastUrl = localStorage.getItem("lastUserListUrl");
        navigate(lastUrl || "/Portal/Users/Manage");
        localStorage.removeItem("lastUserListUrl");
      } else {
        showToast("Unable to delete profile", "error");
      }
    } catch (error) {
      console.error(`Error processing Add request:`, error);
      showToast("Unable to delete profile", "error");
    }
  };

  const handleNav = () => {
    const lastUrl = localStorage.getItem("lastUserListUrl");
    if (lastUrl) {
      navigate(lastUrl);
      localStorage.removeItem("lastUserListUrl");
    } else {
      navigate("/Portal/Users/Manage");
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Stack spacing={1} sx={{ py: 2 }}>
        <Button
          variant="text"
          onClick={handleNav}
          startIcon={<ChevronLeftIcon />}
          sx={{
            alignSelf: "flex-start",
            color: "black",
            textTransform: "none",
            fontWeight: 600,
            px: 0,
          }}
        >
          Back
        </Button>

        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
            Edit User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update this user’s details, password settings, or remove their account.
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress color="primary" size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading user details...
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
            <Userdetails
              theme={theme}
              userInfo={userInfo}
              editProfile={updateProfile}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              width: { xs: "100%", lg: 380 },
              flexShrink: 0,
              alignSelf: { lg: "flex-start" },
              position: { lg: "sticky" },
              top: { lg: 16 },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <ManageAccountsOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                User actions
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manage password access or permanently remove this user.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <LockOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Password settings
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <ActionRow
                    title="Set password"
                    description="Force set a new password for this user."
                    action={
                      <ForceSetPassword email={userInfo?.email} theme={theme} />
                    }
                  />
                  <ActionRow
                    title="Send reset email"
                    description="Email this user a password reset link."
                    action={
                      <ResetPassword data={userInfo} theme={theme} />
                    }
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <DeleteOutlineIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Delete account
                  </Typography>
                </Stack>
                <ActionRow
                  title="Delete user"
                  description={
                    isDeleteDisabled
                      ? "This account is protected and cannot be deleted."
                      : "Permanently remove this user from the portal."
                  }
                  action={
                    <DeleteUser
                      theme={theme}
                      deleteUser={deleteUser}
                      disabled={isDeleteDisabled}
                    />
                  }
                />
              </Box>
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default EditUser;
